import { StringMap } from './utils';
import { AdDimension } from './googleAds';

export enum CmsSlotName {
  MAIN = 'main',
  BELOW_TITLE = 'below_title',
  ABOVE_SORTING = 'above_sorting',
  ABOVE_REFINEMENTS_ROW = 'above_refinements_row',
  BELOW_PRODUCTS = 'below_products',
  ABOVE_FOOTER = 'above_footer',
  ABOVE_TITLE = 'above_title',
  SEO_SLOT = 'seo_slot',
  ABOVE_LOAD_MORE = 'above_load_more',
  BELOW_LOAD_MORE = 'below_load_more',
  BELOW_FILTERS_SIDEBAR_DESKTOP = 'below_filters_sidebar_desktop',
}

export enum RecommendationAppearance {
  SLIDER = 'Slider',
  GRID = 'Grid',
}

export type AbTest = Partial<BaseEntity> & {
  name: string;
  // minimum distribution factor
  min: number;
  // maximum distribution factor
  max: number;
};

export type AbTestModule = {
  abTest?: AbTest;
};

export type BaseEntity = {
  id: string;
  contentType: CmsContentType;
};

export type BaseModule = AbTestModule & Partial<BaseEntity>;

export enum ImageStyle {
  ROUNDED_SHADOW = 'rounded corners & shadow',
}

export type BaseModuleImage = BaseModule & {
  title: string;
  image: ResponsiveImage;
  cta?: CallToAction;
  imageStyle?: ImageStyle;
};

export type CallToAction = Partial<BaseEntity> & {
  title?: string;
  url?: string;
  target?: string;
  rel?: string[];
};

export enum CmsModuleType {
  PRODUCTS = 'module-products',
  NEWSLETTER = 'module-newsletter',
  CAROUSEL = 'module-carousel',
  SEARCH = 'module-search',
  CATEGORIES = 'module-categories',
  SOCIAL_MEDIA = 'module-social-media',
  IMAGE_BANNER = 'module-image-banner',
  TEXT = 'module-text',
  AD = 'module-ad',
  TEASER = 'module-teaser',
  ROW = 'module-row',
  HEADLINE = 'module-headline',
  ERROR = 'module-error',
  SOCIAL_EMBED = 'module-social-embed',
  TAG_TEASER = 'module-tag-teaser',
  SLOT = 'module-slot',
  PRODUCTS_V2 = 'module-products-v2',
  JUMP_TO_SECTION = 'module-jumpToSectionAnchor',
  DY_CONTAINER = 'module-dy-container',
  DY_SLIDER_CONTAINER = 'module-dy-slider-container',
  REDIRECT = 'redirect',
  DY_CAMPAIGN = 'module-dy-campaign',
  UC_PRIVACY_POLICY = 'module-uc-privacy-policy',
}

export enum CmsEntityType {
  CAROUSEL_ITEM = 'module-carousel-item',
  CATEGORIES_ITEM = 'module-categories-item',
  TEASER_ITEM = 'module-teaser-item',
  AB_TEST = 'abTest',
  CTA = 'callToActionCta',
  PAGE = 'page',
  PAGE_META = 'pageMeta',
  RESPONSIVE_IMAGE = 'responsiveImage',
}

export type CmsContentType = CmsEntityType | CmsModuleType;

export type ModuleAd = BaseModule & {
  adUnitPath: string;
  sizes: AdDimension[];
};

export type ModuleUcPrivacyPolicy = BaseModule & {
  settingId: string;
};

export type ModuleCarousel = BaseModule & {
  items: ModuleCarouselItem[];
  autoplay?: boolean;
  autoplayInterval?: number;
  withoutControls?: boolean;
  wrapAround?: boolean;
};

export type ModuleCarouselItem = BaseModuleImage;

export enum ModuleCategoriesStyle {
  CATEGORIES = 'categories',
  LOGOS = 'logos',
}

export enum Appearance {
  DEFAULT = 'default',
  SLIDER = 'slider',
}

export type ModuleCategories = BaseModule & {
  title: string;
  titleTag?: HeadlineTag;
  style: ModuleCategoriesStyle;
  items: ModuleCategoriesItem[];
  cta?: CallToAction;
  showAllCategoryButton?: any;
  appearance?: Appearance;
};

export type ModuleCategoriesItem = BaseModule & {
  title: string;
  image: string;
  cta: CallToAction;
};

export type ModuleError = BaseModule & {
  message: string;
};

export enum HeadlineTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
  H6 = 'h6',
}
export enum HeadlineStyle {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
}

export type ModuleHeadline = BaseModule & {
  as?: HeadlineTag;
  style?: HeadlineStyle;
  color?: string;
  borderColor?: string;
  text?: string;
};

export type ModuleImageBanner = BaseModuleImage;

export type ModuleNewsletter = BaseModule;

export type ModuleProducts = BaseModule & {
  title: string;
  prudsysArea?: string; // PrudsysRecommendationChannel;
  productIds?: string[];
  query?: string;
  filters?: string[];
  categoryId?: string;
  appearence: string; // RecommendationAppearance;
  productsPerPage: number;
};

export type ModuleProductsV2 = BaseModule & {
  title: string;
  appearence: RecommendationAppearance;
  productsPerPage: number;
  sortingOrderOfIds: SortingOrderOfIds;
  showLoadmore: ShowLoadmore;
  categoryId?: string;
  query?: string;
  algoliaSorting: string;
  concatenateIdsWithDynamicQueries: ConcatenateIdsWithDynamicQueries;
  productIds?: string[];
  filters?: string[];
};

export enum SortingOrderOfIds {
  DEFAULT = 'default_sorting',
  MANUAL = 'manual_sorting',
}

export enum ShowLoadmore {
  TRUE = 'yes',
  FALSE = 'no',
}

export enum ConcatenateIdsWithDynamicQueries {
  TRUE = 'yes',
  FALSE = 'no',
}

type ResponsiveColumn = number | number[];

export type ModuleRow = BaseModule & {
  modules: BaseModule[];
  columns: ResponsiveColumn[];
};

export type ModuleSearch = BaseModule & {
  autoSuggest?: boolean;
};

export enum SocialEmbedType {
  FACEBOOK = 'facebook',
  PINTEREST = 'pinterest',
  YOUTUBE = 'youtube',
  INSTAGRAM = 'instagram',
}

export type ModuleSocialEmbed = BaseModule & {
  type: SocialEmbedType;
  embedCode: string;
};

export enum SocialMediaType {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  PINTEREST = 'pinterest',
}

export type SocialMediaItem = {
  id: SocialMediaType;
  url: string;
};

export type ModuleSocialMedia = BaseModule & {
  title: string;
  items: SocialMediaItem[];
};

export type ModuleTagTeaser = BaseModule & {
  image?: ModuleImageBanner;
  text?: string;
  items?: CallToAction[];
  cta: CallToAction;
};

export type ModuleTeaser = BaseModule & {
  title: string;
  items: ModuleTeaserItem[];
  cta: CallToAction;
};

export type ModuleTeaserItem = BaseModule & {
  title: string;
  image: string;
  cta: CallToAction;
};

export type ModuleText = BaseModule & {
  body: any;
  textAlign?: 'left' | 'center' | 'right';
};

export type ModuleSlotMap = { [key in CmsSlotName]: BaseModule[] | undefined };

export type CmsPage = Partial<BaseEntity> & {
  url: string;
  title: string;
  modules: ModuleSlotMap;
  meta: PageMeta;
  breadcrumbs?: CallToAction[];
  customBreadcrumbs?: string;
};

export type PageMeta = Partial<BaseEntity> & {
  metaDescription?: string;
  robots?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
};

export type ResponsiveImageSize = {
  breakpoint: string; // Breakpoint;
  src: string;
};

export type ResponsiveImage = Partial<BaseEntity> & {
  src: string;
  alt: string;
  title: string;
  sizes: ResponsiveImageSize[];
  ratio: number[];
};

export type Slot = Partial<BaseEntity> & {
  name: CmsSlotName;
};

export type SimpleFieldTransform = string;
export type CustomFieldTransform = {
  key: string;
  getter: (obj: any) => any;
  defaultTransform?: boolean;
};

export type Entity = ModuleCarouselItem | AbTest | CallToAction | CmsPage | PageMeta | ResponsiveImage;

export type Module = ModuleCarousel | ModuleProducts | ModuleNewsletter | ModuleSearch | ModuleAd;

export enum CmsPreview {
  STATIC = 'static',
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export type FieldTransform = SimpleFieldTransform | CustomFieldTransform;

export type Validation = (data: any) => boolean;

export type TransformationConfig = {
  transform: FieldTransform[];
  validate?: Validation;
};

export type FilterConfig = {
  abDistributionFactor?: number;
  preview?: CmsPreview;
};

export type ContentType = Module | Entity;

export type Transform = (c: FilterConfig) => (data: any) => ContentType | null;

export type TransformFields = (
  c: FilterConfig,
) => (fieldTransforms: FieldTransform[], fields: StringMap<any>) => StringMap<any>;

export type TransformField = (c: FilterConfig) => (field: any) => any;

export type TransformFieldArray = (c: FilterConfig) => (field: any[]) => any[];

export type TransformationConfigMap = { [key in CmsContentType]: TransformationConfig };

export type ModuleJumpToSectionAnchor = BaseModule & { hash: string; title?: string };

export type DYContainer = BaseModule & {
  dyContainerId: string;
  title: string;
};

export type DYSliderContainer = BaseModule & {
  dyContainerId: string;
  title: string;
};
